import React from 'react';
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Paper,
  TableContainer,
  Button,
} from '@material-ui/core';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import Table from 'modules/shared-elements/Table';
import Header from '../../components/Header';
import classes from './Bin.module.scss';
import { EmptyState } from './components';
import { buildHierarchical, formatBytes } from 'util/file';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Description, Folder } from '@material-ui/icons';
import moment from 'moment';
import SorterByName from '../Documents/components/SorterByName';
import { ProductBinManagementContext } from 'context';
import Toolbar from '../../components/Toolbar';
import { Delete, History } from '@material-ui/icons';
import ToolbarPlaceholder from './components/ToolbarPlaceholder';
import useNotification from 'hooks/useNotification';
import DeleteModal from '../Documents/components/Modal/DeleteModal';
const BINSection = ({ onRefresh, onViewLocation }) => {
  const {
    data,
    transformedData,
    selected,
    setSelected,
    handleSelect,
    deletePermanently,
    restoreFile,
    emptyBin,
    fetchData,
  } = React.useContext(ProductBinManagementContext);
  const [show] = useNotification();
  const [folders, setFolders] = React.useState([]);
  const [sortName, setSortName] = React.useState('asc');
  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);
  const [openEmptyModal, setOpenEmptyModal] = React.useState(false);

  const isEmpty = React.useMemo(() => {
    return !data.length;
  }, [data]);

  const getHierarchical = React.useCallback((id) => {
    return buildHierarchical(id, data);
  });
  const items = React.useMemo(() => {
    return transformedData.sort((a, b) => {
      const order = sortName === 'asc' ? 1 : -1;
      return a.name.localeCompare(b.name) * order;
    });
  }, [folders, data, transformedData, sortName]);

  const onOpenFolder = (record) => {
    const next = [...folders];
    next.push(record);
    setFolders(next);
  };

  const firstSelected = React.useMemo(() => {
    if (selected.length) {
      const first = selected[0];
      return items.find(({ id: fileFolderId }) => fileFolderId === +first);
    }
    return {};
  }, [selected, items]);

  const handleDelete = async () => {
    const records = selected.map((id) => {
      return items.find(({ id: fileFolderId }) => fileFolderId === +id);
    });
    await deletePermanently(records, () => {
      let message = `${records.length} ${records.length > 1 ? 'items' : 'item'} deleted`;
      show('success', 'delete-permanently-success', message);
    });
    setOpenDeleteModal(false);
  };

  React.useEffect(() => {
    fetchData();
  }, []);
  return (
    <>
      <Box className={classes.bin}>
        <Breadcrumbs separator={<NavigateNextIcon />} className={classes.breadcrumb}>
          <Link color='inherit' href='/product/list'>
            Product List
          </Link>
          <Typography variant='body2'>Bin</Typography>
        </Breadcrumbs>
        <Box display='flex' alignItems='center'>
          <Header title='Bin' hiddenMore />
        </Box>
        {!isEmpty && (
          <Toolbar className={classes.toolbar}>
            <Box className={classes.content}>
              {!!selected.length ? (
                <>
                  <Toolbar.Action
                    leftIcon={<History />}
                    onClick={() => {
                      const records = selected.map((id) => {
                        return items.find(({ id: fileFolderId }) => fileFolderId === +id);
                      });
                      restoreFile(records, () => {
                        setSelected([]);
                        onRefresh();
                        show(
                          'success',
                          'restore-success',
                          `${records.length} items restored`,
                          records.length < 2
                            ? {
                                actions: (
                                  <>
                                    <Button
                                      type='text'
                                      color='primary'
                                      onClick={() => {
                                        onViewLocation(records[0].location);
                                      }}>
                                      View location
                                    </Button>
                                  </>
                                ),
                              }
                            : {}
                        );
                      });
                    }}>
                    Restore
                  </Toolbar.Action>
                  <Toolbar.Action
                    onClick={() => {
                      setOpenDeleteModal(true);
                    }}
                    leftIcon={<Delete />}>
                    Delete permanently
                  </Toolbar.Action>
                </>
              ) : (
                <ToolbarPlaceholder></ToolbarPlaceholder>
              )}
            </Box>
            {!!selected.length ? (
              <span>{`${selected.length} selected`}</span>
            ) : (
              <Toolbar.Action
                onClick={() => {
                  // emptyBin();
                  setOpenEmptyModal(true);
                }}
                leftIcon={<Delete />}>
                Empty bin
              </Toolbar.Action>
            )}
          </Toolbar>
        )}

        {isEmpty ? (
          <EmptyState />
        ) : (
          <Box mt={'24px'}>
            <TableContainer className={classes.container__table} component={Paper}>
              <Table
                className={classes.table}
                rowSelection={{
                  selection: selected,
                  onSelect: (record, newSelected) => {
                    handleSelect(`${newSelected}`);
                  },
                  onSelectAll: (newSelected, selectedRows) => {
                    setSelected(newSelected);
                  },
                }}
                columns={[
                  {
                    title: 'Name',
                    key: 'name',
                    name: 'name',
                    dataIndex: 'name',
                    width: '40%',
                    size: 'medium',
                    filterIcon: <ExpandMoreIcon />,
                    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => {
                      return (
                        <SorterByName
                          value={selectedKeys[0] || {}}
                          onChange={({ key }) => {
                            selectedKeys[0] = key;
                            setSortName(key);
                            setSelectedKeys([...selectedKeys]);
                            setTimeout(() => {
                              confirm();
                            }, 500);
                          }}
                        />
                      );
                    },
                    render: (value, record) => {
                      const { url } = record;
                      return (
                        <Box display='flex' alignItems='center' className={classes.fileName}>
                          {url ? <Description /> : <Folder />}
                          <span>{value}</span>
                        </Box>
                      );
                    },
                  },
                  {
                    title: 'File Size',
                    key: 'size',
                    name: 'size',
                    dataIndex: 'size',
                    width: '140px',
                    sorter: (a, b) => (a.size || 0) - (b.size || 0),
                    render: (value, record) => {
                      if (!record.url) {
                        const folder = getHierarchical(record.id);
                        return <>{formatBytes(folder[record.id]?.size || 0)}</>;
                      }
                      return <>{formatBytes(value || 0)}</>;
                    },
                  },
                  {
                    title: 'Date Deleted',
                    key: 'deletedAt',
                    name: 'deletedAt',
                    dataIndex: 'deletedAt',
                    sorter: (a, b) => {
                      return moment(a.updatedAt).toDate() - moment(b.updatedAt);
                    },
                    render: (value) => {
                      return <>{moment(value).format('DD/MM/YYYY')}</>;
                    },
                  },
                  {
                    title: 'Original Location',
                    key: 'location',
                    name: 'location',
                    dataIndex: 'location',

                    render: (value = []) => {
                      return <>{value.join('/')}</>;
                    },
                  },
                ]}
                dataSource={[...items]}
                pagination={{
                  total: items.length || 0,
                }}
                getRowKey={(record) => record?.id}
                onRow={(record) => {
                  return {
                    onClick: () => {
                      if (!record.url) {
                        return;
                      }
                      window.open(record.previewUrl, '_blank').focus();
                    },
                  };
                }}
              />
            </TableContainer>
          </Box>
        )}
      </Box>
      <DeleteModal
        onDelete={handleDelete}
        handleClose={() => {
          setOpenDeleteModal(false);
        }}
        open={openDeleteModal}
        title={`Delete ${selected.length > 1 ? selected.length + ' items' : ''} Permanently ?`}
        actionDeleteName='Delete permanently'
        body={
          <Typography className={classes.contentModal} variant='body1'>
            Once confirmed, <b>{firstSelected?.name ?? ''}</b>{' '}
            {selected.length > 1 ? `and ${selected.length - 1} items ` : ''}will be gone forever and
            you won’t be able to restore {selected.length > 1 ? ' it' : ' them'}.
          </Typography>
        }></DeleteModal>
      <DeleteModal
        onDelete={() => {
          emptyBin(() => {
            show(
              'success',
              'empty-bin-success',
              `${data.length} ${data.length > 1 ? 'items' : 'item'} deleted`
            );
            setOpenEmptyModal(false);
          });
        }}
        handleClose={() => {
          setOpenEmptyModal(false);
        }}
        open={openEmptyModal}
        title={`Empty bin?`}
        actionDeleteName='Delete permanently'
        body={
          <Typography className={classes.contentModal} variant='body1'>
            Once confirmed, all items in the bin will be gone forever and you won’t be able to
            restore them.
          </Typography>
        }></DeleteModal>
    </>
  );
};
export default BINSection;
